// src/components/StrategyBuilder/StrategyBuilder.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Typography,
  Stack,
  Alert,
  alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import strategyAPI from '../../services/strategyService';
import { transformStrategyForAPI, validateStrategy } from '../../utils/strategyTransform';
import StepStrategyInfo from './StepStrategyInfo';
import StepDefineSignals from './StepDefineSignals';
import StepConditions from './StepConditions';
import StepConfirmation from './StepConfirmation';
import StepReview from './StepReview';

const steps = [
  'Strategy Info',
  'Define Signals',
  'Conditions',
  'Confirmation',
  'Review',
];

const StrategyBuilder = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { strategyId } = useParams();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [strategyData, setStrategyData] = useState({
    strategy_id: '',
    strategy_name: '',
    symbol: '',
    timeframe: '1m',
    description: '',
    expected_signals: [],
    conditions: [],
    confirmation_condition_index: null,
    entry_logic: 'all',
    alert_config: {
      alert_on_entry: true,
    },
  });

  // Load strategy data if editing
  useEffect(() => {
    if (isEdit && strategyId) {
      loadStrategyData();
    }
  }, [isEdit, strategyId]);

  const loadStrategyData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await strategyAPI.getStrategyState(strategyId);
      setStrategyData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (finalData) => {
    try {
      // Pre-submission validation with detailed logging
      console.log('=== PRE-SUBMISSION VALIDATION ===');
      console.log('Raw Strategy Data:', JSON.stringify(finalData, null, 2));
      
      // Check for empty or invalid conditions
      const invalidConditions = finalData.conditions?.filter(c => {
        if (!c.name || c.name.trim() === '') {
          console.warn('⚠️ Found condition with no name:', c);
          return true;
        }
        if (!c.type) {
          console.warn('⚠️ Found condition with no type:', c);
          return true;
        }
        if (c.type === 'time_bound' || c.type === 'interference') {
          if (!c.trigger || !c.trigger.algo || !c.trigger.direction) {
            console.warn('⚠️ Found condition with invalid trigger:', c);
            return true;
          }
          if (!c.target || !c.target.algo || !c.target.direction) {
            console.warn('⚠️ Found condition with invalid target:', c);
            return true;
          }
        }
        if (c.type === 'simultaneous') {
          if (!c.signals || c.signals.length < 2) {
            console.warn('⚠️ Found condition with insufficient signals:', c);
            return true;
          }
        }
        return false;
      });
      
      if (invalidConditions && invalidConditions.length > 0) {
        const errorMsg = `Found ${invalidConditions.length} invalid condition(s). Please check all conditions are properly configured.`;
        console.error('❌ VALIDATION FAILED:', errorMsg);
        console.error('Invalid conditions:', invalidConditions);
        throw new Error(errorMsg);
      }
      
      // Transform frontend data to exact backend API format
      const apiData = transformStrategyForAPI(finalData);
      
      console.log('=== TRANSFORMATION ===');
      console.log('Transformed API Data:', JSON.stringify(apiData, null, 2));
      
      // Final validation on transformed data
      apiData.conditions?.forEach((condition, idx) => {
        console.log(`Condition ${idx + 1} (${condition.signal_name}):`, {
          type: condition.type,
          trigger: condition.trigger,
          target: condition.target,
          signal1: condition.signal1,
          signal2: condition.signal2,
        });
      });
      
      console.log('=== SUBMITTING TO BACKEND ===');
      const response = await strategyAPI.setupStrategy(apiData);
      
      console.log('✅ Backend Response:', response);
      
      // Show success message and navigate
      alert(`Strategy ${isEdit ? 'updated' : 'created'} successfully!`);
      navigate('/admin/strategy-builder');
      
    } catch (err) {
      console.error('❌ Strategy submission error:', err);
      console.error('Error details:', err.response?.data || err.message);
      throw err;
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepStrategyInfo
            data={strategyData}
            onChange={setStrategyData}
          />
        );
      case 1:
        return (
          <StepDefineSignals
            data={strategyData}
            onChange={setStrategyData}
          />
        );
      case 2:
        return (
          <StepConditions
            data={strategyData}
            onChange={setStrategyData}
          />
        );
      case 3:
        return (
          <StepConfirmation
            data={strategyData}
            onChange={setStrategyData}
          />
        );
      case 4:
        return (
          <StepReview
            data={strategyData}
            onSubmit={handleSubmit}
            isEdit={isEdit}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        // Strategy info validation
        return strategyData.strategy_id && 
               strategyData.strategy_id.trim() !== '' &&
               strategyData.symbol &&
               strategyData.symbol.trim() !== '';
      case 1:
        // Signals validation
        return strategyData.expected_signals && 
               strategyData.expected_signals.length >= 2;
      case 2:
        // Conditions validation
        const hasConditions = strategyData.conditions && 
                             strategyData.conditions.length > 0;
        if (!hasConditions) return false;
        
        // Check that all conditions are properly configured
        return strategyData.conditions.every(condition => {
          if (!condition.type) return false;
          
          if (condition.type === 'simultaneous') {
            return condition.signals && condition.signals.length >= 2;
          }
          
          if (condition.type === 'time_bound' || condition.type === 'interference') {
            return condition.trigger && 
                   condition.trigger.algo && 
                   condition.target && 
                   condition.target.algo;
          }
          
          return true;
        });
      case 3:
        // Confirmation validation
        return strategyData.confirmation_condition_index !== null &&
               strategyData.confirmation_condition_index !== undefined;
      default:
        return true;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Loading strategy data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            {isEdit ? 'Edit Strategy' : 'Create New Strategy'}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Build sophisticated trading strategies with multi-signal conditions
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '2px solid #e2e8f0' }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontWeight: activeStep === index ? 700 : 500,
                      color: activeStep === index ? '#667eea' : '#64748b',
                    },
                    '& .MuiStepIcon-root': {
                      color: activeStep === index ? '#667eea' : '#cbd5e1',
                      '&.Mui-completed': {
                        color: '#10b981',
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            border: '2px solid #e2e8f0',
            minHeight: 400,
          }}
        >
          {getStepContent(activeStep)}
        </Paper>

        {/* Navigation Buttons */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e2e8f0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<BackIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#64748b',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                },
              }}
            >
              Back
            </Button>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Step {activeStep + 1} of {steps.length}
              </Typography>
            </Stack>

            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed()}
                endIcon={<ForwardIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  background: canProceed()
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#cbd5e1',
                  boxShadow: canProceed()
                    ? '0 4px 12px rgba(102, 126, 234, 0.25)'
                    : 'none',
                  '&:hover': {
                    background: canProceed()
                      ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                      : '#cbd5e1',
                  },
                  '&:disabled': {
                    color: 'white',
                  },
                }}
              >
                Next
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default StrategyBuilder;